import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

const AboutSection = () => {
  const sectionRef = useRef();
  const lineRef = useRef();
  const labelRef = useRef();
  const headingRef = useRef();
  const imagesRef = useRef([]);

  useEffect(() => {
    // Animation de la ligne
    gsap.fromTo(lineRef.current,
      { scaleX: 0, opacity: 0 },
      {
        scaleX: 1,
        opacity: 1,
        duration: 1.5,
        ease: "power3.out",
        scrollTrigger: {
          trigger: lineRef.current,
          start: "top 80%",
          toggleActions: "play none none reverse"
        }
      }
    );

    // Animation du label
    gsap.fromTo(labelRef.current,
      { y: 30, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 1,
        delay: 0.3,
        ease: "power2.out",
        scrollTrigger: {
          trigger: labelRef.current,
          start: "top 85%",
          toggleActions: "play none none reverse"
        }
      }
    );

    // Animation du heading avec effet de reveal
    const headingWords = headingRef.current.querySelectorAll('.word');
    gsap.fromTo(headingWords,
      { y: 60, opacity: 0, rotateX: -20 },
      {
        y: 0,
        opacity: 1,
        rotateX: 0,
        duration: 0.8,
        stagger: 0.03,
        ease: "power3.out",
        scrollTrigger: {
          trigger: headingRef.current,
          start: "top 85%",
          toggleActions: "play none none reverse"
        }
      }
    );

    // Animation des images du carrousel
    const images = imagesRef.current;
    images.forEach((img, index) => {
      if (img) {
        // Animation d'entrée
        gsap.fromTo(img,
          { 
            y: 80, 
            opacity: 0,
            scale: 0.9,
            rotateY: -15
          },
          {
            y: 0,
            opacity: 1,
            scale: 1,
            rotateY: 0,
            duration: 1,
            delay: index * 0.05,
            ease: "power3.out",
            scrollTrigger: {
              trigger: img,
              start: "top 95%",
              toggleActions: "play none none reverse"
            }
          }
        );

        // Hover effect
        img.addEventListener('mouseenter', () => {
          gsap.to(img, {
            scale: 1.05,
            y: -15,
            zIndex: 10,
            duration: 0.4,
            ease: "power2.out"
          });
        });

        img.addEventListener('mouseleave', () => {
          gsap.to(img, {
            scale: 1,
            y: 0,
            zIndex: 1,
            duration: 0.4,
            ease: "power2.out"
          });
        });
      }
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const addToImagesRef = (el) => {
    if (el && !imagesRef.current.includes(el)) {
      imagesRef.current.push(el);
    }
  };

  // Images étiquetées avec des noms descriptifs
  const imagesData = [
    { id: 3, path: "/assets/3.jpg", alt: "Fashion shoot" },
    { id: 12, path: "/assets/12.jpg", alt: "Stage performance" },
    { id: 13, path: "/assets/13.jpg", alt: "Fashion editorial" },
    { id: 14, path: "/assets/14.jpg", alt: "Interview moment" },
    { id: 15, path: "/assets/15.jpg", alt: "Recording studio" },
    { id: 16, path: "/assets/16.jpg", alt: "Red carpet" },
    { id: 17, path: "/assets/17.jpg", alt: "Music video" },
    { id: 18, path: "/assets/18.jpg", alt: "Photoshoot outdoor" },
    { id: 19, path: "/assets/19.jpg", alt: "TV show" },
    { id: 20, path: "/assets/20.jpg", alt: "Concert crowd" },
    { id: 21, path: "/assets/21.jpg", alt: "Studio portrait" },
    { id: 22, path: "/assets/22.jpg", alt: "Fashion week" },
    { id: 23, path: "/assets/23.jpg", alt: "Press conference" },
    { id: 24, path: "/assets/24.jpg", alt: "Live session" },
    { id: 25, path: "/assets/25.jpg", alt: "Backstage prep" },
    { id: 26, path: "/assets/26.jpg", alt: "TV interview" },
    { id: 27, path: "/assets/27.jpg", alt: "Music festival" },
    { id: 28, path: "/assets/28.jpg", alt: "Modeling campaign" },
    { id: 29, path: "/assets/29.jpg", alt: "Final performance" }
  ];

  // Split text en mots pour l'animation
  const headingText = "Safaa Shiey is a talented Moroccan soul and R&B singer, as well as a model, actress and television reporter.";
  const words = headingText.split(' ');

  return (
    <section 
      ref={sectionRef}
      id="about"
      className="relative bg-white py-16 lg:py-24 overflow-hidden" // Espace réduit
    >
      {/* Ligne décorative en haut */}
      <div className="px-6 lg:px-8">
        <div 
          ref={lineRef}
          className="w-full h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent max-w-7xl mx-auto origin-center"
        />
      </div>

      {/* Espacement réduit */}
      <div className="h-12 lg:h-16" />

      {/* Contenu principal */}
      <div className="px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          
          {/* En-tête About */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 mb-12 lg:mb-20"> {/* Espace réduit */}
            
            {/* Label */}
            <div className="lg:col-span-3">
              <div 
                ref={labelRef}
                className="flex items-center space-x-4"
              >
                <div className="w-12 h-px bg-gradient-to-r from-purple-400 to-pink-400" />
                <h2 className="text-sm font-bold text-gray-700 tracking-widest uppercase">
                  About
                </h2>
              </div>
            </div>

            {/* Heading principal avec animation mot par mot */}
            <div className="lg:col-span-9">
              <h3 
                ref={headingRef}
                className="text-2xl lg:text-4xl xl:text-5xl font-light text-gray-900 leading-tight" // Taille réduite
                style={{ perspective: '1000px' }}
              >
                {words.map((word, index) => (
                  <span key={index} className="word inline-block mr-2" style={{ transformStyle: 'preserve-3d' }}>
                    {word}
                  </span>
                ))}
              </h3>
            </div>

          </div>

        </div>
      </div>

      {/* Espacement réduit avant le carrousel */}
      <div className="h-12 lg:h-16" />

      {/* Carrousel d'images avec labels */}
      <div className="relative">
        <div className="carousel-wrapper overflow-hidden">
          <div className="carousel-track flex space-x-4 animate-scroll"> {/* Espace entre images réduit */}
            {/* Première série d'images */}
            {imagesData.map((image) => (
              <div
                key={image.id}
                ref={addToImagesRef}
                className="carousel-item flex-none w-64 h-80 rounded-xl overflow-hidden cursor-pointer group relative" // Taille réduite
                style={{ transformStyle: 'preserve-3d' }}
              >
                <img
                  src={image.path}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />

                {/* Overlay au hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                {/* Bordure décorative */}
                <div className="absolute inset-0 border border-white/0 group-hover:border-white/30 rounded-xl transition-all duration-500" />
              </div>
            ))}

            {/* Deuxième série (dupliquée pour scroll infini) */}
            {imagesData.map((image) => (
              <div
                key={`${image.id}-copy`}
                className="carousel-item flex-none w-64 h-80 rounded-xl overflow-hidden cursor-pointer group relative"
                style={{ transformStyle: 'preserve-3d' }}
              >
                <img
                  src={image.path}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                
                {/* Label de l'image */}
                <div className="absolute top-3 left-3 bg-black/70 text-white px-2 py-1 rounded text-xs font-medium opacity-90 group-hover:opacity-100 transition-opacity duration-300">
                  {image.label}
                </div>
                
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute inset-0 border border-white/0 group-hover:border-white/30 rounded-xl transition-all duration-500" />
              </div>
            ))}
          </div>
        </div>

        {/* Gradients de fade sur les côtés */}
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-white via-white/80 to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white via-white/80 to-transparent z-10 pointer-events-none" />
      </div>

      {/* Espacement final réduit */}
      <div className="h-12 lg:h-16" />

      <style jsx>{`
        .carousel-wrapper {
          mask-image: linear-gradient(
            to right,
            transparent,
            black 8%,
            black 92%,
            transparent
          );
          -webkit-mask-image: linear-gradient(
            to right,
            transparent,
            black 8%,
            black 92%,
            transparent
          );
        }

        .animate-scroll {
          animation: scroll 50s linear infinite; /* Vitesse ajustée */
        }

        .carousel-track:hover {
          animation-play-state: paused;
        }

        .carousel-item {
          box-shadow: 
            0 15px 30px -8px rgba(0, 0, 0, 0.1),
            0 8px 16px -4px rgba(0, 0, 0, 0.05);
        }

        .carousel-item:hover {
          box-shadow: 
            0 25px 50px -12px rgba(0, 0, 0, 0.2),
            0 12px 24px -8px rgba(0, 0, 0, 0.1);
        }

        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(calc(-50%));
          }
        }

        .word {
          transform-origin: center bottom;
          backface-visibility: hidden;
        }

        @media (max-width: 1024px) {
          .carousel-item {
            width: 14rem;
            height: 18rem;
          }

          .animate-scroll {
            animation: scroll 70s linear infinite;
          }
        }

        @media (max-width: 640px) {
          .carousel-item {
            width: 12rem;
            height: 16rem;
          }
        }
      `}</style>
    </section>
  );
};

export default AboutSection;